import React, { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Dialog, TextField } from '@material-ui/core'
import MekomeTablePagination from 'components/MekomeTablePagination'
import { useTranslation } from 'react-i18next'
import { theme, Button, Text, Spinner } from 'ui'
import { observer } from 'mobx-react'
import useStore from 'store'
import MessagesHeader from 'components/Messages/MessagesHeader'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { UserGroup } from 'utils/types'
import type { CsvUser } from 'utils/models'
import AddCsvScreen from './AddCsvScreen'
import CsvListScreen from './CsvListScreen'

const ContainerDialog = styled.div`
    flex-direction:column;
    display:flex;
    height:80vh;
    max-height:1000px;
    width:90vw;
    max-width:1400px;
`
const StepOneContainer = styled.div`
    flex:1;
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
    gap:22px;
    padding:40px;
`
const GroupSelectWrap = styled.div`
    width:360px;
    max-width:90%;
`
const ButtonsContainer = styled.div`
    display:flex;
    justify-content:space-between;
    width:100%;
    padding:10px;
    align-items:flex-end;
`
const ConfirmButton = styled(Button) <{ bgColor: string, hoverColor?: string }>`
  &.MuiButtonBase-root {
    font-size: ${theme.fontSize.large};
    font-weight: 500;
    min-width: 140px;
    height: 50px;
    margin: 0.5rem;
    background:${p => p.bgColor};
    &:hover {
      background: ${p => p.hoverColor ? p.hoverColor : theme.color.darkBlue};
    }
    color: ${theme.color.white};
    &.Mui-disabled {
      opacity: 0.5;
    }
  }
`

const AddMembersToGroupModal: FC = () => {
    const { orgs, csvStore, groups } = useStore()
    const { t } = useTranslation('members')

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [loading, setLoading] = useState(false)
    const [step, setStep] = useState<1 | 2>(1)
    // Keep the chosen group in local state: CsvListScreen's clear button resets
    // relateToGroupsId, so we (re)apply it at confirm time to be safe.
    const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null)

    useEffect(() => {
        if (csvStore.uploaded) {
            csvStore.resolveCsvUserList(page, rowsPerPage)
        }
    }, [csvStore.uploaded])

    const closeModal = () => {
        csvStore.closeGroupImportModal()
        setStep(1)
        setSelectedGroup(null)
        setPage(0)
    }

    const handleOk = async () => {
        setLoading(true)
        // createUsers() adds the created users to relateToGroupsId when it's set.
        csvStore.setRelateToGroup(selectedGroup as UserGroup)
        await csvStore.createUsers()
        setLoading(false)
    }

    const isSendDisabled = () => {
        return csvStore.getCsvUsers.filter((csvUser: CsvUser) => csvUser.selected).length === 0
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
        csvStore.resolveCsvUserList(newPage, rowsPerPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
        csvStore.resolveCsvUserList(1, parseInt(event.target.value, 10))
    }

    const groupName = selectedGroup?.group?.name

    return (
        <Dialog
            style={{ zIndex: 1200, overflow: 'hidden' }}
            open={csvStore.groupImportModalOpen}
            maxWidth={'xl'}
        >
            <ContainerDialog>
                <MessagesHeader
                    disableIcon
                    textVarient={'black'}
                    backColor={theme.color.azureBlue + 33}
                    mainText={orgs.currentOrg.name}
                    secondText={step === 2 && groupName ? `${t`addManyToGroup`} — ${groupName}` : t`addManyToGroup`}
                    onClose={closeModal}
                />

                {step === 1 &&
                    <StepOneContainer>
                        <Text align={'center'} textcolor={theme.color.black + 99} weight={'medium'} size={'lg'}>
                            {t`app:choose_group_for_members`}
                        </Text>
                        <GroupSelectWrap>
                            <Autocomplete
                                value={selectedGroup}
                                onChange={(_: any, v: any) => setSelectedGroup(v)}
                                options={[...(groups.currentUserGroups ?? [])].sort((a: UserGroup, b: UserGroup) => Number(a.group.group_is_member === false) - Number(b.group.group_is_member === false))}
                                renderOption={(option: any) => option?.group?.name}
                                getOptionLabel={(option: any) => option?.group?.name || ''}
                                renderInput={(params) =>
                                    <TextField {...params} label={t`csvHeaders:addToGroup`} variant="outlined" />}
                            />
                        </GroupSelectWrap>
                        <ConfirmButton
                            disabled={!selectedGroup}
                            bgColor={selectedGroup ? theme.color.confirmButton : theme.color.confirmButton + 33}
                            onClick={() => setStep(2)}
                        >
                            {t`app:continue`}
                        </ConfirmButton>
                    </StepOneContainer>
                }

                {step === 2 &&
                    <>
                        {csvStore.rawUsersToImport.length === 0 ?
                            <AddCsvScreen /> :
                            <CsvListScreen />}

                        <MekomeTablePagination
                            style={{ direction: 'ltr' }}
                            labelRowsPerPage=''
                            rowsPerPageOptions={[50, 100]}
                            component="div"
                            count={csvStore.rawUsersToImport.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />

                        <ButtonsContainer>
                            {csvStore.rawUsersToImport.length != 0 &&
                                <ConfirmButton
                                    disabled={loading ? true : isSendDisabled()}
                                    bgColor={isSendDisabled() ? theme.color.confirmButton + 33 : theme.color.confirmButton}
                                    onClick={handleOk}
                                >
                                    {loading && <Spinner color="inherit" size="2rem" />}
                                    {!loading && t`app:add`}
                                </ConfirmButton>}
                        </ButtonsContainer>
                    </>
                }
            </ContainerDialog>
        </Dialog>
    )
}

export default observer(AddMembersToGroupModal)
